import type { QuizReviewQuestion } from "@/client"
import { FeedbackIcon } from "@/components/Feedback/FeedbackIcon"
import { FeedbackModal } from "@/components/Feedback/FeedbackModal"
import { useColorModeValue } from "@/components/ui/color-mode"
import { useFeedbackEnabled } from "@/hooks/useFeedbackEnabled"
import { formatPercentage } from "@/utils/format-percentage"
import { stripOptionPrefix } from "@/utils/text-format"
import {
  Badge,
  Box,
  Card,
  HStack,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useState } from "react"
import { HtmlContent } from "../Common/HtmlContent"
import { QuizQuestionSaveNoteModal } from "./QuizQuestionSaveNoteModal"
import {
  type QuestionOvertimeEntry,
  formatClock,
} from "./StudentTimer/studentTimer"

interface QuestionReviewProps {
  question: QuizReviewQuestion
  quizId?: string
  isTeacher?: boolean
  questionNumber?: number
  /** Overtime against the student's personal per-question timer, if any. */
  paceOvertime?: QuestionOvertimeEntry
}

export function QuestionReview({
  question,
  quizId,
  isTeacher = false,
  questionNumber,
  paceOvertime,
}: QuestionReviewProps) {
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const { enabled: feedbackEnabled } = useFeedbackEnabled()
  const correctOptionBg = useColorModeValue("green.50", "green.900")
  const incorrectOptionBg = useColorModeValue("red.50", "red.900")
  const highlightedOptionText = useColorModeValue("gray.800", "gray.50")
  const highlightedOptionBorder = useColorModeValue("green.200", "green.700")
  const highlightedIncorrectBorder = useColorModeValue("red.200", "red.700")
  const explanationBg = useColorModeValue("blue.50", "blue.900")
  const explanationBorder = useColorModeValue("blue.200", "blue.700")
  const explanationText = useColorModeValue("gray.800", "gray.50")

  const isUnanswered = question.user_answer === null

  return (
    <Card.Root p={6}>
      <Stack gap={4}>
        <HStack justify="space-between" align="start">
          <Box flex={1}>
            {isUnanswered ? (
              <Badge colorPalette="yellow" mb={2}>
                Not answered
              </Badge>
            ) : (
              <Badge
                colorPalette={question.is_correct ? "green" : "red"}
                mb={2}
              >
                {question.is_correct ? "Correct" : "Incorrect"}
              </Badge>
            )}
            {questionNumber !== undefined && (
              <Heading size="md" mb={2}>
                Question {questionNumber}
              </Heading>
            )}
            <HtmlContent
              fontSize="lg"
              fontWeight="medium"
              content={question.question_text}
            />
          </Box>

          {quizId && (
            <HStack align="start">
              <QuizQuestionSaveNoteModal quizId={quizId} question={question} />
              {feedbackEnabled && (
                <FeedbackIcon
                  feedbackCount={question.feedback_count}
                  onClick={() => setFeedbackModalOpen(true)}
                />
              )}
            </HStack>
          )}
        </HStack>

        <Stack gap={2}>
          {question.options.map((option: string, optionIndex: number) => {
            const isUserAnswer = question.user_answer === optionIndex
            const isCorrectAnswer = question.correct_answer === optionIndex
            const peerPercentage =
              question.peer_answer_percentages?.[optionIndex]
            const peerPercentageText =
              typeof peerPercentage === "number"
                ? `${formatPercentage(
                    peerPercentage,
                  )}% of students chose this option`
                : null

            return (
              <Box
                key={optionIndex}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor={
                  isCorrectAnswer
                    ? highlightedOptionBorder
                    : isUserAnswer
                      ? highlightedIncorrectBorder
                      : "border"
                }
                bg={
                  isCorrectAnswer
                    ? correctOptionBg
                    : isUserAnswer
                      ? incorrectOptionBg
                      : "transparent"
                }
                color={
                  isCorrectAnswer || isUserAnswer
                    ? highlightedOptionText
                    : undefined
                }
              >
                <Text>
                  {String.fromCharCode(65 + optionIndex)}.{" "}
                  {stripOptionPrefix(option)}
                  {isUserAnswer && " (Your answer)"}
                  {isCorrectAnswer && " ✓"}
                </Text>
                {peerPercentageText && (
                  <Text fontSize="sm" color="fg.muted" mt={1}>
                    {peerPercentageText}
                  </Text>
                )}
              </Box>
            )
          })}
        </Stack>

        {question.rationale && (
          <Box
            p={4}
            bg={explanationBg}
            borderRadius="md"
            borderWidth="1px"
            borderColor={explanationBorder}
            color={explanationText}
          >
            <Text fontWeight="semibold">Explanation:</Text>
            <HtmlContent content={question.rationale} />
          </Box>
        )}

        <HStack gap={3} flexWrap="wrap">
          <Text fontSize="sm" color="fg.muted">
            Time spent: {question.time_spent_seconds}s
          </Text>
          {paceOvertime && (
            <Text fontSize="sm" color="yellow.fg">
              Over your {formatClock(paceOvertime.limitSeconds)} target by{" "}
              {formatClock(paceOvertime.overtimeSeconds)}
            </Text>
          )}
        </HStack>
      </Stack>

      {quizId && feedbackEnabled && (
        <FeedbackModal
          open={feedbackModalOpen}
          onClose={() => setFeedbackModalOpen(false)}
          quizId={quizId}
          questionId={question.question_id}
          isTeacher={isTeacher}
        />
      )}
    </Card.Root>
  )
}
